import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { ReactNode } from 'react';

interface ResponsiveActionButtonProps {
    /** The text label shown on desktop viewports */
    label: string;
    /** The icon element to display */
    icon: ReactNode;
    /** Click handler */
    onClick: () => void;
    /** Button color variant (default: 'secondary') */
    color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
    /** Whether the button is disabled */
    disabled?: boolean;
    /** Optional aria-label override (defaults to label) */
    ariaLabel?: string;
}

/**
 * A responsive action button that shows icon+text on desktop and icon-only on mobile.
 *
 * On mobile viewports (≤768px), the button shows only the icon with a tooltip.
 * On desktop viewports, the button shows the icon followed by the text label.
 *
 * Follows the neo-brutalist design system with:
 * - Zero border radius
 * - Bold 2px border
 * - Uppercase text with letter spacing
 * - Heavy font weight
 *
 * @example
 * ```tsx
 * <ResponsiveActionButton
 *     label="Add Property"
 *     icon={<AddIcon />}
 *     onClick={handleCreate}
 * />
 * ```
 */
const ResponsiveActionButton = ({
    label,
    icon,
    onClick,
    color = 'secondary',
    disabled = false,
    ariaLabel,
}: ResponsiveActionButtonProps) => {
    const isMobile = useMediaQuery('(max-width:768px)');

    const buttonContent = isMobile ? icon : label;
    const effectiveAriaLabel = ariaLabel ?? label;

    const button = (
        <Button
            variant="contained"
            color={color}
            startIcon={isMobile ? undefined : icon}
            onClick={onClick}
            disabled={disabled}
            aria-label={effectiveAriaLabel}
            sx={{
                borderRadius: 0,
                border: '2px solid',
                borderColor: `${color}.main`,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                px: isMobile ? 1.5 : 3,
                py: 1,
                minWidth: isMobile ? 'auto' : undefined,
            }}
        >
            {buttonContent}
        </Button>
    );

    // On mobile, wrap with tooltip to show the label on hover/long-press
    if (isMobile) {
        return (
            <Tooltip title={label} arrow>
                {button}
            </Tooltip>
        );
    }

    return button;
};

export default ResponsiveActionButton;
